import ContinuationNote from './ContinuationNote';
import type { DiagnosisContinuation, DiagnoseEnvelope } from '../../apis';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useVoyagerStore from '../../stores/useVoyagerStore';
import { formatKpiValue } from '../agent-common/formatKpiValue';
import { adjustClaim, createAnnotation, getAllPerspectives, getModel, listAnnotations, runDiagnoseForClaim, type AdjustResult, type Annotation } from '../../apis';
import { bucketDate, cellOf, claimKey, groundingLine, noteQueryForClaim, rowLabel, type Claim } from './claims';
import { flattenModel } from '../../extension/adapters';
import { analysesOfSection, pinCandidates, pinDestination, reviewHasKey, reviewSections, turnCount, sectionClaimKeys, priorKeyWindow, refreshedClaim } from '../../stores/slices/reviewsSlice';
import TierChip from './TierChip';
import type { ReviewSection } from '../../types';
import { verdictFamily } from '../../extension/adapters';
import NumberDrawer, { NumberSparkline } from './NumberDrawer';
import VerdictEditor from './VerdictEditor';
import { targetOfClaim } from './contributions';
import { layerCorrection } from './corrections';
import { contextFromClaim } from '../../stores/slices/landingAgentSlice';
import { answerOfClaim, claimRestsOnAReading } from '../../stores/slices/pinsSlice';

/** A reconcile verdict in the reader's terms — what it says about THEIR number. */
const VERDICT_WORD: Record<string, string> = {
  match: 'matches', ambiguous: 'matches (which metric?)', stale: 'stale',
  mismatch: "doesn't match", unknown_term: 'to define', error: "couldn't check", skipped: 'not checked',
};
const reconcileVerdictWord = (v: string) => VERDICT_WORD[v] || "couldn't check";

/** The durable artifact a number sits in (#196): a saved review's section. A chat number has none. */
export interface ClaimArtifact { kind: 'review'; thread_id: string; section_id: string }

interface Anchor {
  claim: Claim;
  /** An element to anchor to, or a screen position (chart points). */
  el?: HTMLElement | null;
  position?: { left: number; top: number } | null;
  artifact?: ClaimArtifact | null;
}

interface PopoverApi {
  open: (claim: Claim, el?: HTMLElement | null, position?: { left: number; top: number } | null, artifact?: ClaimArtifact | null) => void;
  close: () => void;
}

const Ctx = createContext<PopoverApi>({ open: () => {}, close: () => {} });

export function useNumberPopover(): PopoverApi {
  return useContext(Ctx);
}

/** The note's title is its first line, capped; the body keeps everything. */
export function noteTitle(text: string): string {
  const first = text.trim().split(/\r?\n/)[0] || 'Note';
  return first.length > 80 ? `${first.slice(0, 79)}…` : first;
}

/** What a note is: an annotation of kind `note` bound to the claim. */
export function noteFor(claim: Claim, text: string): Partial<Annotation> & Record<string, any> {
  const g = claim.grounding;
  // A note written on ONE bucket of a time series belongs to that bucket, not to the end of
  // the chart's window: stamping a July point with August 31 files it under August, where an
  // August aggregate finds it and nothing about the note says otherwise.
  // …but only when the bucket IS a date. A comparison cell's period is a label
  // ("2026-09-01 → 2026-09-07"), which the server would reject as a timestamp — and a note
  // that cannot be saved is worse than one filed a few days late.
  const ts = bucketDate(g) || g?.window?.end || g?.window?.start || new Date().toISOString().slice(0, 10);
  // A dimension predicate is a scalar the matcher can compare. A null cell
  // (a row for "no region") is part of the claim's identity — the key keeps
  // it — but it is not a predicate, so it stays out of the scope.
  // The same cell the reader asks with (`noteQueryForClaim`): a total filtered to West is the
  // West cell even with no row, and scoping it metric-wide would put this note on East.
  const dims = Object.fromEntries(Object.entries(cellOf(g))
    .filter(([, v]) => v != null && ['string', 'number', 'boolean'].includes(typeof v)));
  return {
    kind: 'note',
    ts: ts.length === 10 ? `${ts}T00:00:00Z` : ts,
    title: noteTitle(text),
    body: text.trim(),
    source: 'note',
    source_ref: claimKey(claim),
    // the bytes' address, never the bytes: the server strips a quote too (#296)
    ...(claim.span ? { provenance: { span: Object.fromEntries(Object.entries(claim.span).filter(([k]) => k !== 'quote')) } } : {}),
    scope: {
      // A derived column has no qualified id; its name still scopes the
      // note to THIS number rather than to every metric in the window.
      ...((g?.metric_qid || g?.metric) ? { metrics: [g.metric_qid || g.metric!] } : {}),
      ...(Object.keys(dims).length ? { dims } : {}),
    },
    confidence: 1,
  };
}

/**
 * The number popover (#183): grounding on every value, and the four actions.
 * Why walks from the number's own grounding (#184); What's next goes to the
 * agent in words (P0.9 replaces that); Pin lands in the Home pins queue until
 * a living review exists (P1.1); Annotate saves a signed note on the number.
 */
export function NumberPopoverHost({ children }: { children: React.ReactNode }) {
  const [anchor, setAnchor] = useState<Anchor | null>(null);
  const open = useCallback((claim: Claim, el?: HTMLElement | null, position?: { left: number; top: number } | null, artifact?: ClaimArtifact | null) => {
    setAnchor({ claim, el: el ?? null, position: position ?? null, artifact: artifact ?? null });
  }, []);
  const close = useCallback(() => setAnchor(null), []);
  const api = useMemo(() => ({ open, close }), [open, close]);
  return (
    <Ctx.Provider value={api}>
      {children}
      {anchor && <NumberPopover anchor={anchor} onClose={close} />}
    </Ctx.Provider>
  );
}

/**
 * "Not right? Adjust" (#196): metric over the catalog, perspective, window,
 * one filter → the number is re-derived under that grounding and graded
 * against its own value. With a review section behind it, the correction
 * is remembered for the review as a signed source mapping.
 */
function AdjustForm({ claim, artifact, onCancel, onDone }: { claim: Claim; artifact: ClaimArtifact | null; onCancel: () => void; onDone: (r: AdjustResult) => void }) {
  const g = claim.grounding!;
  const [perspectives, setPerspectives] = useState<{ id: string; name?: string; display_name?: string }[]>([]);
  const [perspective, setPerspective] = useState(g.perspective || '');
  const [metrics, setMetrics] = useState<{ qid: string; label: string; draft?: boolean }[]>([]);
  const [dimensions, setDimensions] = useState<{ name: string; label: string }[]>([]);
  const [metric, setMetric] = useState(g.metric_qid || '');
  const [start, setStart] = useState(g.window?.start || '');
  const [end, setEnd] = useState(g.window?.end || '');
  const [dimension, setDimension] = useState('');
  const [filterValue, setFilterValue] = useState('');
  const [remember, setRemember] = useState(!!artifact);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getAllPerspectives().then((rows: any[]) => { if (!cancelled) setPerspectives(rows || []); }).catch(() => { if (!cancelled) setPerspectives([]); });
    return () => { cancelled = true; };
  }, []);
  useEffect(() => {
    if (!perspective) return undefined;
    let cancelled = false;
    getModel(perspective).then((model: any) => {
      if (cancelled) return;
      // the whole inheritance tree: a metric two models deep is still a metric the perspective answers with
      const flat = flattenModel(model);
      const opts = flat.metrics.map((m) => ({ qid: m.qid, label: m.draft ? `${m.label} (draft)` : m.label, draft: m.draft }));
      setMetrics(opts);
      if (!opts.some((o) => o.qid === metric)) setMetric(opts[0]?.qid || '');
      setDimensions(flat.dimensions);
    }).catch((e: any) => { if (!cancelled) setError(e?.response?.data?.error || e?.message || 'Could not load the catalog'); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perspective]);

  const submit = async () => {
    if (!metric || !perspective || busy) return;
    setBusy(true); setError(null);
    try {
      // a review section is named; a chat number names nothing — it is previewed, and the server says where a mapping could live
      const res = await adjustClaim({
        artifact,
        claim: { id: claim.id, label: claim.label || g.metric || 'number', value: claim.value, unit: unitOf(claim.format), window: start && end ? { start, end } : (g.window?.start && g.window?.end ? { start: g.window.start, end: g.window.end } : null) },
        grounding: { metric_qid: metric, perspective, filters: dimension && filterValue.trim() ? [{ dimension, operator: '=', value: filterValue.trim() }] : [], window: start && end ? { start, end } : null },
        remember: !!artifact && remember && !metrics.find((m) => m.qid === metric)?.draft,
      });
      onDone(res);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'The re-grounding did not run');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ mt: 1 }} data-testid="adjust-form">
      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>
        {artifact ? 'This is a correction, not a one-off: this review will ground the number here first next time.' : 'Preview only — a chat number has no source to remember; pin it to a review to teach the layer.'}
      </Typography>
      <TextField select fullWidth size="small" label="Perspective" value={perspective} onChange={(e) => setPerspective(e.target.value)} sx={{ mb: 1 }} inputProps={{ 'aria-label': 'Perspective' }} SelectProps={{ native: true }}>
        {!perspectives.some((p) => p.id === perspective) && perspective && <option value={perspective}>{perspective}</option>}
        {perspectives.map((p) => <option key={p.id} value={p.id}>{p.display_name || p.name || p.id}</option>)}
      </TextField>
      <TextField select fullWidth size="small" label="Metric" value={metric} onChange={(e) => setMetric(e.target.value)} sx={{ mb: 1 }} inputProps={{ 'aria-label': 'Metric' }} SelectProps={{ native: true }}>
        {!metrics.some((m) => m.qid === metric) && metric && <option value={metric}>{metric}</option>}
        {metrics.map((m) => <option key={m.qid} value={m.qid}>{m.label}</option>)}
      </TextField>
      <Box sx={{ display: 'flex', gap: 0.75, mb: 1 }}>
        <TextField size="small" type="date" label="From" value={start} onChange={(e) => setStart(e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ 'aria-label': 'Window start' }} sx={{ flex: 1 }} />
        <TextField size="small" type="date" label="To" value={end} onChange={(e) => setEnd(e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ 'aria-label': 'Window end' }} sx={{ flex: 1 }} />
      </Box>
      <Box sx={{ display: 'flex', gap: 0.75, mb: 0.5 }}>
        <TextField select size="small" label="Filter" value={dimension} onChange={(e) => setDimension(e.target.value)} sx={{ flex: 1 }} inputProps={{ 'aria-label': 'Filter dimension' }} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}>
          <option value="">— none —</option>
          {dimensions.map((d) => <option key={d.name} value={d.name}>{d.label}</option>)}
        </TextField>
        <TextField size="small" label="Value" value={filterValue} onChange={(e) => setFilterValue(e.target.value)} disabled={!dimension} sx={{ flex: 1 }} inputProps={{ 'aria-label': 'Filter value' }} />
      </Box>
      {artifact && (
        <FormControlLabel sx={{ ml: 0 }} control={<Checkbox size="small" checked={remember && !metrics.find((m) => m.qid === metric)?.draft} disabled={!!metrics.find((m) => m.qid === metric)?.draft} onChange={(e) => setRemember(e.target.checked)} inputProps={{ 'aria-label': 'Remember for this review' } as any} />}
          label={<Typography variant="caption">{metrics.find((m) => m.qid === metric)?.draft ? 'A draft definition is previewed, not remembered — publish it first' : 'Remember for this review'}</Typography>} />
      )}
      {error && <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }} data-testid="adjust-error">{error}</Typography>}
      <Box sx={{ display: 'flex', gap: 0.75, mt: 0.75, justifyContent: 'flex-end' }}>
        <Button size="small" onClick={onCancel}>Cancel</Button>
        <Button size="small" variant="contained" disabled={!metric || !perspective || busy} onClick={submit}>{busy ? 'Re-grounding…' : 'Re-ground & re-verify'}</Button>
      </Box>
    </Box>
  );
}

/** The claim's format as the unit reconcile grades a bare value by. */
function unitOf(format: string | null | undefined): string | null {
  if (format === 'count' || format === 'integer') return 'int';
  if (format === 'percent') return 'pct';
  if (format === 'currency') return 'usd2';
  return null;
}

/** The popover's first look at a review number (the mock's preview): the significance step alone, never a cause. */
export interface WhyPreview {
  continuation?: DiagnosisContinuation;
  kind: 'significant' | 'within_normal' | 'insufficient_data' | 'error';
  headline: string;
  z?: number | null;
  threshold?: number | null;
  delta_pct?: number | null;
  /** the traced session the preview wrote — none when it was read off the review's own refresh */
  session_id?: string | null;
  /** where the numbers came from: the review's last refresh, or a preview run just now */
  from: 'review' | 'run';
}

const fmtPct = (v: number | null | undefined) => (v == null || Number.isNaN(v) ? '' : `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`);

/**
 * A review that has refreshed already ran the significance step on this
 * number; its answer is the preview, with no query. Otherwise null and the
 * server is asked for the step alone.
 */
export function previewFromSection(section: ReviewSection | null | undefined): WhyPreview | null {
  const sig = section?.why?.significance;
  if (!sig?.status) return null;
  const z = typeof sig.z === 'number' ? sig.z : null;
  const zText = z == null ? '' : `z = ${z.toFixed(1)}`;
  const thr = typeof sig.threshold === 'number' ? ` (threshold ${sig.threshold.toFixed(1)})` : '';
  const move = sig.delta_pct != null ? `, ${fmtPct(sig.delta_pct)} vs prior period` : '';
  if (sig.status === 'significant') {
    return { kind: 'significant', z, threshold: sig.threshold ?? null, delta_pct: sig.delta_pct ?? null, from: 'review',
      headline: `Significant — ${zText}${thr}${move}. The full walk runs in its own tab.` };
  }
  if (sig.status === 'within_normal') {
    return { kind: 'within_normal', z, threshold: sig.threshold ?? null, delta_pct: sig.delta_pct ?? null, from: 'review',
      headline: `Within its normal range${zText ? ` (${zText}${thr}${move})` : ''} — no statistically significant movement against this baseline.` };
  }
  if (sig.status === 'insufficient_data') {
    return { kind: 'insufficient_data', z, threshold: sig.threshold ?? null, delta_pct: sig.delta_pct ?? null, from: 'review',
      headline: 'Not enough history to judge this window — the full walk can say more.' };
  }
  // A refresh whose step errored (no rows, a warehouse that went away) is
  // not an answer to show: the preview runs fresh instead.
  return null;
}

/** The preview the server ran: its verdict as the popover's line, its significance detail, its session. */
export function previewFromEnvelope(env: any): WhyPreview {
  const sig = (env?.steps || []).find((st: any) => st?.id === 'significance')?.detail || {};
  const kind = env?.verdict?.kind;
  return {
    kind: kind === 'preview' ? 'significant' : kind === 'within_normal' ? 'within_normal' : kind === 'insufficient_data' ? 'insufficient_data' : 'error',
    headline: env?.verdict?.headline || 'The significance step did not answer.',
    z: typeof sig.z === 'number' ? sig.z : null,
    threshold: typeof sig.threshold === 'number' ? sig.threshold : null,
    delta_pct: typeof sig.delta_pct === 'number' ? sig.delta_pct : null,
    session_id: env?.session_id || null,
    from: 'run',
    continuation: env?.continuation,
  };
}

function correctionEnvelope(claim: Claim, why: ReviewSection['why']): DiagnoseEnvelope | null {
  const target = targetOfClaim(claim, claim.grounding?.perspective || null);
  if (!target || !why?.verdict?.headline) return null;
  const storedDriver = why.driver as any;
  const driver = storedDriver?.id || storedDriver?.label ? [{
    kind: why.driver?.kind || 'text', id: why.driver?.id || why.driver?.label || '', label: why.driver?.label || why.driver?.id || '',
    title: why.driver?.label || why.driver?.id || '', ts: '', tier: why.driver?.tier || 'unconfirmed', sources: [],
    ...(storedDriver.kind === 'segment' && storedDriver.metric ? { metric: storedDriver.metric } : {}),
    ...Object.fromEntries(['capped_by', 'edge_status', 'edge_provenance', 'annotation_kind', 'proposed_by']
      .filter((name) => storedDriver[name] != null).map((name) => [name, storedDriver[name]])),
  }] : [];
  const storedLayers = (why as any)?.verdict_layers;
  return {
    target: { metric: target.metric, window_start: target.window_start, window_end: target.window_end, filters: target.filters as any },
    verdict: { kind: (why.verdict.kind || 'explained') as any, headline: why.verdict.headline, confidence: 0 },
    chain: [], segments: null, annotations: [], alternatives: [], residual_note: null,
    budget: { limit: 0, queries: 0, effort: 'quick' }, drivers: driver as any, session_id: why.session_id || null,
    ...(Array.isArray(storedLayers) && storedLayers.length ? { verdict_layers: storedLayers } : {}),
  };
}

type SavedCorrection = Annotation & Record<string, any>;

function orderedCorrections(rows: SavedCorrection[]): SavedCorrection[] {
  const unique = Array.from(new Map(rows.map((row) => [row.id, row])).values());
  return unique.sort((a: any, b: any) => {
    const at = a.provenance?.asserted_at || a.created_at || '';
    const bt = b.provenance?.asserted_at || b.created_at || '';
    return String(at).localeCompare(String(bt)) || String(a.id).localeCompare(String(b.id));
  });
}

function applyCorrections(envelope: DiagnoseEnvelope | null, corrections: SavedCorrection[]): DiagnoseEnvelope | null {
  if (!envelope || corrections.length === 0) return envelope;
  const ordered = orderedCorrections(corrections);
  let current = envelope;
  if (!current.verdict_layers?.length && current.verdict.kind === 'corrected') {
    // Review documents intentionally keep only the current summary verdict, and
    // correction annotations intentionally redact the original headline. Keep a
    // truthful placeholder for that immutable first layer instead of treating the
    // already-corrected summary as the original or inventing redacted text.
    const original = { kind: 'unexplained' as const, headline: 'Original verdict preserved in the trace.', confidence: 0 };
    current = { ...current, verdict: original as any, verdict_layers: [{ layer: 'original', verdict: original }] };
  }
  const existing = new Set((current.verdict_layers || []).map((layer: any) => layer.id).filter(Boolean));
  return ordered.filter((row) => !existing.has(row.id)).reduce((result, row) => layerCorrection(result, row), current);
}

function NumberPopover({ anchor, onClose }: { anchor: Anchor; onClose: () => void }) {
  const sendLandingAgentQuery = useVoyagerStore((s) => s.sendLandingAgentQuery);
  const askWhyForClaim = useVoyagerStore((s) => s.askWhyForClaim);
  const openVerdictPanel = useVoyagerStore((s) => s.openVerdictPanel);
  const loadThread = useVoyagerStore((s) => s.loadThread);
  // a thread opened from the popover leaves the page the verdict panel was opened on — it goes too
  const leave = () => { onClose(); useVoyagerStore.getState().closeVerdictPanel(); };
  // The analysis already opened from this number, newest first: a new walk
  // is a choice beside resuming it, never the default that hides it.
  const latestAnalysis = useVoyagerStore((s) => (anchor.artifact?.kind === 'review' ? analysesOfSection(s.savedThreads, anchor.artifact.section_id)[0] ?? null : null));
  const pinClaim = useVoyagerStore((s) => s.pinClaim);
  const currentThreadId = useVoyagerStore((s) => s.currentThreadId);
  const [mode, setMode] = useState<'actions' | 'annotate' | 'adjust' | 'preview' | 'correct' | 'span'>('actions');
  const [adjusted, setAdjusted] = useState<AdjustResult | null>(null);
  const [preview, setPreview] = useState<WhyPreview | null>(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  // The review section this number sits in (#189), for the preview: a
  // refreshed review already ran the significance step and holds the walk's verdict.
  const section = useVoyagerStore((s) => (anchor.artifact?.kind === 'review'
    ? reviewSections(s.savedThreads.find((t) => t.id === anchor.artifact!.thread_id)).find((x) => x.id === anchor.artifact!.section_id) ?? null
    : null));
  const claim = useMemo(() => section?.claim ? refreshedClaim(section) : anchor.claim, [section, anchor.claim]);
  const span = claim.span;
  const statistic = claim.statistic;
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notes, setNotes] = useState<Annotation[] | null>(null);
  const [savedCorrections, setSavedCorrections] = useState<SavedCorrection[]>([]);
  const [restoredEnvelope, setRestoredEnvelope] = useState<DiagnoseEnvelope | null>(null);
  const key = claimKey(claim);
  const exactKeys = JSON.stringify(sectionClaimKeys(section, key));
  // Where a pin would land (#189): the review on screen, else the living
  // review most recently changed, else the Home queue. The button says so.
  const destination = useVoyagerStore((s) => span || statistic ? null : pinDestination(s.savedThreads, s.currentThreadId, claim.grounding?.perspective));
  // Pinned is a fact about the store, not about this popover: a number that
  // was pinned last week opens as pinned today.
  const pinned = useVoyagerStore((s) => (destination ? reviewHasKey(destination, key) : s.pins.some((p) => p.key === key)));
  // More than one living review could take this number: the person picks, the app never guesses.
  const pinOptions = useVoyagerStore((s) => pinCandidates(s.savedThreads, claim.grounding?.perspective));
  const candidates = span || statistic ? [] : pinOptions;
  const pinClaimToReview = useVoyagerStore((s) => s.pinClaimToReview);
  const queuePin = useVoyagerStore((s) => s.queuePin);
  const [pinMenu, setPinMenu] = useState<HTMLElement | null>(null);
  const label = claim.label || claim.grounding?.metric || 'this number';
  const subject = rowLabel(claim) ? `${label} for ${rowLabel(claim)}` : label;
  const envelope = correctionEnvelope(claim, section?.why || null);
  const shownEnvelope = applyCorrections(restoredEnvelope || envelope, savedCorrections);

  // Notes already on this number: kind `note`, bound by the claim key. They
  // are fetched on open, so a reload finds what was saved.
  const grounded = !!claim.grounding || !!span;
  // A pin lands in Home's pins queue (#182). A review refreshes a pin from
  // its grounding (#189): only a catalog metric with a dated window can be one.
  const refreshable = !!(claim.grounding?.metric_qid && claim.grounding?.window?.start && claim.grounding?.window?.end);
  // #1818: a number whose answer rests on a reading of a meaning no person has settled is not pinned anywhere
  const answer = useVoyagerStore((s) => answerOfClaim(s.landingAgentTurns, claim));
  const unsettled = useVoyagerStore((s) => claimRestsOnAReading(s.landingAgentTurns, claim));
  const canPin = grounded && (refreshable || !!span) && !unsettled;
  const pinSource = { thread_id: currentThreadId, ...(answer?.sessionId ? { session_id: answer.sessionId } : {}) };
  // Adjust (#196): a number with a value and a dated window can be re-grounded
  // — the metric, the perspective, the window, a filter — and re-verified
  // live. It is REMEMBERED only on a durable artifact (a review's section);
  // a chat number is previewed, never taught, because nothing recurring is
  // behind it.
  const canAdjust = typeof claim.value === 'number' && refreshable && !statistic;
  useEffect(() => {
    let cancelled = false;
    // An ungrounded number never has notes: its key names it only inside
    // its own answer, so nothing durable is bound to it.
    if (!grounded) { setNotes([]); return () => { cancelled = true; }; }
    // Filtered on the server, so an old note on this number cannot fall off a
    // recency-limited global list. Two questions are asked, because a note can be
    // addressed two ways: our own surfaces (and an MCP note that names its window) bind
    // it to the claim KEY, while Slack writes a scope and no key — and reading by the key
    // alone hid a colleague's note on the very number it was about. Ranked mode matches
    // the way the walk does.
    // notes and confirmations both bind to the number by its key (#197): who said this verdict is right, beside who left a note
    const ranked = noteQueryForClaim(claim);
    Promise.all([
      Promise.all((['note', 'confirmation'] as const).map((kind) => {
        const keys = JSON.parse(exactKeys) as string[];
        return listAnnotations({ kind, source_ref: keys.length === 1 ? keys[0] : keys, limit: kind === 'confirmation' ? 50 : 200 }).catch(() => [] as Annotation[]);
      })).then((rows) => rows.flat()),
      ranked ? listAnnotations({ kind: 'note', ...ranked, limit: 200 }).catch(() => [] as Annotation[]) : Promise.resolve([] as Annotation[]),
    ])
      .then(([exact, about]) => {
        if (cancelled) return;
        const keys = new Set<string>(JSON.parse(exactKeys));
        const mine = Array.from(new Map(exact.filter((a) => a.source_ref && keys.has(a.source_ref)).map((a) => [a.id, a])).values());
        // One note, one entry: a note our surface wrote carries the key AND matches the
        // metric, so it comes back from both questions.
        const seen = new Set(mine.map((a) => a.id));
        // Ranking is inclusive on purpose — scope raises rank, it never filters, because a walk
        // wants to weigh an event scoped elsewhere and say why it set it aside. A reader looking
        // at ONE number does not: `routed` is the server's own word for "routed to this metric",
        // and without it a note about support tickets would sit under Orders.
        const routed = about.filter((a: any) => a.match_reasons?.routed && !seen.has(a.id));
        setNotes([...mine, ...routed]);
        // Persisted correction layers are restored by the diagnosis endpoint,
        // after the server applies the current viewer's evidence policy. Only
        // corrections saved in this open drawer are layered locally.
      })
      .catch(() => { if (!cancelled) setNotes([]); });
    return () => { cancelled = true; };
  }, [key, exactKeys, grounded, claim]);

  // A number pinned in a review (#189): a question from it opens as its own
  // thread, stamped with the section it came from — the store leaves the review.
  const source = anchor.artifact?.kind === 'review' ? { kind: 'review_section', id: anchor.artifact.section_id } : null;
  const ask = (text: string) => {
    leave();
    // a question about a number is asked about THAT number (#767): in its perspective and its scope, for this
    // question only — the thread's pick is never switched for the asker
    const own = claim.grounding?.perspective || undefined;
    const scope = contextFromClaim(claim);
    sendLandingAgentQuery(text, { ...(source ? { source } : {}), ...(own ? { perspectiveId: own } : {}),
      ...(scope ? { threadContext: scope } : {}) });
  };
  // WHY from a number (#184): a re-derived number walks straight from its
  // grounding — no words re-parsed. A number with no catalog metric behind
  // it (a derived column, an ungrounded tile) asks in words instead.
  // The envelope stamps a relative range with its dates; a claim with a
  // preset and no dates predates that and would walk the wrong days.
  const walkable = !!(claim.grounding?.metric_qid && claim.grounding?.window?.start && claim.grounding?.window?.end);
  const openWalk = (intent: 'why' | 'next' = 'why') => {
    leave();
    if (walkable) askWhyForClaim(claim, subject, source, { intent });
    else ask(intent === 'next' ? `What should we do about ${subject}?` : `Why did ${subject} change?`);
  };
  // "Why this verdict" (the mock): the walk behind the line opens in the side
  // panel — the review's stored walk when it holds one, else the preview's test.
  const showVerdictPanel = () => {
    if (!preview) return;
    onClose();
    openVerdictPanel({
      kind: 'section', title: subject, reviewTitle: null, claim, perspective: claim.grounding?.perspective || null,
      why: section?.why?.verdict?.headline
        ? { ...section.why, preview }
        : { significance: { status: preview.kind, z: preview.z, threshold: preview.threshold, delta_pct: preview.delta_pct }, session_id: preview.session_id, preview },
    });
  };
  // WHY on a walkable number is a preview first (the mock): the significance
  // step — read off the review's own refresh when it has one, else run alone
  // — with the full walk one click away in its own tab.
  const why = () => {
    if (span) { setMode('span'); return; }
    if (!walkable) { openWalk(); return; }
    setMode('preview');
    const known = previewFromSection(section);
    if (known) {
      setPreview(known);
      // A review stores only its current summary verdict. When that verdict
      // has corrections, restore the immutable layers through a fresh server
      // walk so matching, driver metadata, and the current viewer's evidence
      // policy are all applied at the authoritative boundary.
      if (section?.why?.verdict?.kind === 'corrected' && !restoredEnvelope) {
        const perspective_id = claim.grounding?.perspective || null;
        runDiagnoseForClaim(claim, { perspective_id, question: `Restore: ${subject}` })
          .then((env) => setRestoredEnvelope(env))
          .catch(() => undefined);
      }
      return;
    }
    setPreview(null); setPreviewBusy(true);
    const perspective_id = claim.grounding?.perspective || null;
    runDiagnoseForClaim(claim, { preview: true, perspective_id, question: `Preview: is ${subject} significant?` })
      .then((env) => setPreview(previewFromEnvelope(env)))
      .catch((e: any) => setPreview({ kind: 'error', headline: e?.response?.data?.error || e?.message || 'The significance step did not run', from: 'run' }))
      .finally(() => setPreviewBusy(false));
  };

  const save = async () => {
    const text = note.trim();
    if (!text || saving) return;
    setSaving(true); setError(null);
    try {
      const saved = await createAnnotation(noteFor(claim, text));
      setNotes((prev) => [saved, ...(prev || [])]);
      setNote(''); setMode('actions');
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Could not save the note');
    } finally {
      setSaving(false);
    }
  };

  const value = formatKpiValue(claim.value, claim.format);
  const historyValues = [...((section?.history || []).map((h: any) => h?.value)), section?.value ?? claim.value]
    .map(Number).filter(Number.isFinite);
  const delta = typeof section?.delta === 'number' ? section.delta : null;
  const dataWarning = section?.error
    ? `Data may be late or unavailable: ${section.error}`
    : section?.why?.verdict?.kind === 'data_late' || claim.verdict === 'stale'
      ? (section?.why?.verdict?.headline || 'This value may be stale. Check the source refresh before acting on it.')
      : null;
  const openQueryBuilder = () => {
    const g = claim.grounding;
    if (!g?.perspective || !g.metric_qid) return;
    const target = targetOfClaim(claim, g.perspective, { skipTimeBuckets: true });
    leave();
    void useVoyagerStore.getState().navigateToWorkspaceWithSpec({
      perspective: g.perspective,
      metrics: [g.metric_qid],
      filters: target?.filters || g.filters || [],
      dateRangeStart: target?.window_start?.slice(0, 10) || g.window?.start || null,
      dateRangeEnd: target?.window_end ? new Date(new Date(target.window_end).getTime() - 86400000).toISOString().slice(0, 10) : (g.window?.end || null),
    });
  };

  const content = (
    <>
      {mode === 'actions' && (
        <Box data-testid="number-overview" sx={{ display: 'grid', gap: 1.25 }}>
          <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1 }}>Overview</Typography>
          <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 36, lineHeight: 1, fontWeight: 720, letterSpacing: '-0.035em', fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
            {delta != null && <Typography data-testid="number-delta" sx={{ fontSize: 13, fontWeight: 650, color: delta >= 0 ? 'var(--ok)' : 'var(--bad)' }}>{delta >= 0 ? '+' : ''}{formatKpiValue(delta, claim.format)}</Typography>}
          </Box>
          <NumberSparkline values={historyValues} />
          {dataWarning && <Box data-testid="number-data-warning" sx={{ p: 1.25, borderRadius: 1.5, bgcolor: 'var(--warn-tint)', color: 'var(--warn)', fontSize: 12.5 }}>{dataWarning}</Box>}
        </Box>
      )}
      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }} data-testid="grounding-line">
        {claim.grounding || span ? groundingLine(claim) : `not re-derived · ${Math.round((claim.confidence || 0) * 100)}% ${claim.confidence_basis}`}
      </Typography>
      {notes && notes.length > 0 && (
        <Box sx={{ mt: 1.25, pt: 1, borderTop: 1, borderColor: 'divider' }} data-testid="claim-notes">
          {notes.map((n) => (
            <Box key={n.id} sx={{ fontSize: 12, lineHeight: 1.5, mb: 0.5 }}>
              <Typography component="span" sx={{ fontSize: 12, fontWeight: 600 }}>{n.kind === 'confirmation' ? '✓ confirmed by ' : ''}{(n as any).author_name || (n.provenance as any)?.asserted_by_name || 'note'}</Typography>
              <Typography component="span" sx={{ fontSize: 11, color: 'text.disabled' }}>{' · '}{new Date((n as any).created_at || n.ts).toLocaleDateString()}</Typography>
              {priorKeyWindow(section, key, n.source_ref) && <Typography sx={{ fontSize: 11, color: 'text.secondary' }} data-testid="claim-note-prior-window">{priorKeyWindow(section, key, n.source_ref) === 'an earlier window' ? 'an earlier window' : `Earlier window · ${priorKeyWindow(section, key, n.source_ref)}`}</Typography>}
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{n.body || n.title}</Typography>
            </Box>
          ))}
        </Box>
      )}
      {(claim.caveats || []).length > 0 && (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
          {(claim.caveats || []).map((c, i) => (
            <Chip key={i} label={c} size="small" sx={{ height: 20, fontSize: '0.68rem', bgcolor: 'var(--warn-tint)', color: 'var(--warn)' }} />
          ))}
        </Box>
      )}

      {adjusted && (
        <Box
          sx={{ mt: 1, p: 1, borderRadius: 1, bgcolor: verdictFamily(adjusted.claim.verdict) === 'good' ? 'var(--ok-tint)' : 'var(--bad-tint)', fontSize: 12 }}
          data-testid="adjusted-result"
          data-color-token={verdictFamily(adjusted.claim.verdict) === 'good' ? 'ok-tint' : 'bad-tint'}
        >
          <Typography sx={{ fontSize: 12, fontWeight: 700 }}>Under your grounding · RE-VERIFIED · {reconcileVerdictWord(adjusted.claim.verdict)}</Typography>
          <Typography sx={{ fontSize: 12 }}>{adjusted.claim.reason}</Typography>
          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
            {adjusted.remembered ? `Mapping saved — signed ${adjusted.mapping?.asserted_by_name || 'you'}, scoped to this review.` : (adjusted.note || 'Preview only — not remembered.')}
          </Typography>
        </Box>
      )}
      {mode === 'actions' && (
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
          <Button size="small" variant="contained" onClick={why}>Why?</Button>
          {!span && <Button size="small" variant="outlined" onClick={() => openWalk('next')}>What's next</Button>}
          {canPin && (
            <Button
              size="small"
              variant="outlined"
              disabled={pinned}
              data-testid="popover-pin"
              onClick={(e) => { if (candidates.length > 1) setPinMenu(e.currentTarget); else pinClaim(claim, currentThreadId, answer?.sessionId); }}
            >
              {pinned ? (destination ? `In ${destination.title}` : 'Pinned') : candidates.length > 1 ? 'Pin to…' : (destination ? `Pin to ${destination.title}` : 'Pin')}
            </Button>
          )}
          <Menu open={!!pinMenu} anchorEl={pinMenu} onClose={() => setPinMenu(null)} data-testid="popover-pin-menu">
            {candidates.map((c) => (
              <MenuItem key={c.id} data-testid="popover-pin-choice"
                onClick={() => {
                  setPinMenu(null);
                  void pinClaimToReview(c.id, claim, pinSource).then((section) => { if (!section) queuePin(claim, currentThreadId); });
                }}>{c.title}</MenuItem>
            ))}
          </Menu>
          {grounded && <Button size="small" variant="outlined" onClick={() => setMode('annotate')}>Add a note</Button>}
          {canAdjust && <Button size="small" variant="outlined" onClick={() => setMode('adjust')}>Adjust</Button>}
          {claim.grounding?.metric_qid && <Button size="small" variant="text" onClick={openQueryBuilder}>Open in Query Builder</Button>}
        </Box>
      )}
      {mode === 'span' && span && (
        <Box data-testid="span-popover" sx={{ mt: 1, overflowWrap: 'anywhere' }}>
          <Typography sx={{ fontSize: 13 }}>{span.quote}</Typography>
          <Typography variant="caption">{groundingLine(claim)}</Typography>
          <Button size="small" onClick={() => setMode('actions')}>Back</Button>
        </Box>
      )}
      {mode === 'preview' && (
        <Box sx={{ mt: 1 }} data-testid="why-preview" data-kind={preview?.kind || 'pending'}>
          {previewBusy && <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>Checking significance…</Typography>}
          {preview && (
            <Typography sx={{ fontSize: 12.5, lineHeight: 1.5, color: preview.kind === 'error' ? 'error.main' : 'text.primary' }} data-testid="why-preview-line">
              {preview.headline}
            </Typography>
          )}
          {shownEnvelope?.drivers?.[0] && (
            <Box data-testid="number-verdict-driver" sx={{ mt: 1.25, p: 1.25, border: '1px solid var(--line)', borderRadius: 1.5, bgcolor: 'var(--ground)' }}>
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>Leading driver</Typography>
              <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{shownEnvelope.drivers[0].label || shownEnvelope.drivers[0].id}</Typography>
                {shownEnvelope.drivers[0].tier && <TierChip tier={shownEnvelope.drivers[0].tier} />}
              </Box>
              {shownEnvelope.verdict.kind === 'corrected' && <Typography sx={{ mt: 0.75, fontSize: 12, color: 'text.secondary' }} data-testid="number-corrected-verdict">{shownEnvelope.verdict.headline}</Typography>}
              {(shownEnvelope.verdict_layers || []).length > 1 && <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>{shownEnvelope.verdict_layers!.length} immutable verdict layers</Typography>}
            </Box>
          )}
          <ContinuationNote continuation={preview?.continuation} />
          {latestAnalysis && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: 'text.secondary' }} data-testid="why-preview-resume-note">
              You already have an analysis on this number: {latestAnalysis.title} · {turnCount(latestAnalysis)} turn{turnCount(latestAnalysis) === 1 ? '' : 's'}.
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
            {latestAnalysis && (
              <Button size="small" variant="contained" onClick={() => { leave(); loadThread(latestAnalysis.id); }} data-testid="why-preview-resume">Resume it</Button>
            )}
            <Button size="small" variant={latestAnalysis ? 'outlined' : 'contained'} onClick={() => openWalk('why')} disabled={previewBusy} data-testid="why-preview-walk">{latestAnalysis ? 'Start a new walk' : 'Open the full walk'}</Button>
            <Button size="small" variant="outlined" onClick={showVerdictPanel} disabled={!preview} data-testid="why-this-verdict">Why this verdict</Button>
            {shownEnvelope && <Button size="small" color="error" onClick={() => setMode('correct')}>This is wrong — correct it</Button>}
            <Button size="small" onClick={() => setMode('actions')}>Back</Button>
          </Box>
        </Box>
      )}
      {mode === 'adjust' && (
        <AdjustForm
          claim={claim}
          artifact={anchor.artifact || null}
          onCancel={() => setMode('actions')}
          onDone={(r) => {
            setAdjusted(r); setMode('actions');
            // the review's section was re-pointed on the server: the open document follows
            if (r.remembered && anchor.artifact) { void useVoyagerStore.getState().fetchThreads?.(); }
          }}
        />
      )}
      {mode === 'correct' && shownEnvelope && (
        <Box>
          <Typography variant="overline" sx={{ display: 'block', mb: 1, color: 'text.secondary', letterSpacing: 1 }}>Correct</Typography>
          <VerdictEditor envelope={shownEnvelope} perspective={claim.grounding?.perspective || null} sessionId={shownEnvelope.session_id || null}
            onCancel={() => setMode('preview')} onSaved={(saved) => { setSavedCorrections((current) => orderedCorrections([...current, saved as SavedCorrection])); setMode('preview'); }} />
        </Box>
      )}
      {!grounded && (
        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }} data-testid="ungrounded-note">
          Not re-derived from the data — nothing to pin or annotate until it is.
        </Typography>
      )}
      {mode === 'annotate' && (
        <Box sx={{ mt: 1 }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            multiline
            minRows={1}
            maxRows={4}
            placeholder="Note for this number… (Enter saves)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); save(); } }}
            inputProps={{ 'aria-label': 'Note for this number' }}
          />
          {error && <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>{error}</Typography>}
          <Box sx={{ display: 'flex', gap: 0.75, mt: 0.75, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={() => setMode('actions')}>Cancel</Button>
            <Button size="small" variant="contained" disabled={!note.trim() || saving} onClick={save}>Save note</Button>
          </Box>
        </Box>
      )}
      {pinned && (
        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
          {destination ? `Pinned in ${destination.title}.` : 'Pinned — it waits on Home until a review takes it.'}
        </Typography>
      )}
    </>
  );

  return (
    <NumberDrawer
      metricId={claim.grounding?.metric_qid || claim.id}
      label={subject}
      definition={claim.grounding?.definition}
      window={claim.grounding?.window}
      onClose={onClose}
    >
      {content}
    </NumberDrawer>
  );
}
